import { Directive, ElementRef, HostListener, inject, OnDestroy, output, OutputEmitterRef, signal } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { LayoutService } from '@shared/services/layout.service';
import { AdaptiveTooltipCoordinator } from '@shared/services/adaptive-tooltip-coordinator.service';

/**
 * Tooltip directive that adapts its behavior based on the platform:
 * - Desktop: tooltip is shown on hover (Material default), click triggers the action immediately
 * - Mobile: two-tap pattern - first tap shows the tooltip, second tap triggers the action
 *
 * When another adaptive tooltip is opened or the user clicks outside,
 * this tooltip is reset so the next interaction starts the pattern from scratch.
 *
 * @example
 * <div
 *   [adaptiveTooltip]="'Open external page'"
 *   (triggered)="openLink()">
 *   Content
 * </div>
 */
@Directive({
	selector: '[araAdaptiveTooltip]',
	standalone: true,
	hostDirectives: [
		{
			directive: MatTooltip,
			inputs: [
				'matTooltip: araAdaptiveTooltip',
				'matTooltipPosition',
				'matTooltipShowDelay',
				'matTooltipHideDelay',
				'matTooltipClass',
				'matTooltipDisabled',
			],
		},
	],
})
export class AraAdaptiveTooltipDirective implements OnDestroy {
	public readonly triggered: OutputEmitterRef<void> = output<void>();

	private readonly el: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
	private readonly tooltip: MatTooltip = inject(MatTooltip);
	private readonly layoutService: LayoutService = inject(LayoutService);
	private readonly coordinator: AdaptiveTooltipCoordinator = inject(AdaptiveTooltipCoordinator);
	private readonly tooltipShownOnMobile = signal(false);

	@HostListener('click', ['$event'])
	protected onClick(event: MouseEvent): void {
		// Desktop: rely on Material's hover tooltip and trigger the action on click
		if (!this.layoutService.isMobile()) {
			this.triggered.emit();
			return;
		}

		// so trigger the action immediately on mobile if no tooltip text given.
		if (!this.tooltip.message) {
			this.triggered.emit();
			return;
		}

		event.stopPropagation();

		if (this.tooltipShownOnMobile()) {
			// Second tap on mobile: hide the tooltip and execute the action
			this.reset();
			this.coordinator.clear(this);
			this.triggered.emit();
		} else {
			// First tap on mobile: show the tooltip and become the active one
			// (any previously active tooltip will be reset by the coordinator)
			this.coordinator.setActive(this);
			this.tooltip.show();
			this.tooltipShownOnMobile.set(true);
		}
	}

	@HostListener('document:click', ['$event'])
	protected onDocumentClick(event: MouseEvent): void {
		if (!this.layoutService.isMobile() || !this.tooltipShownOnMobile()) {
			return;
		}

		// Hide the tooltip when the user taps outside the host element
		const clickedInside: boolean = this.el.nativeElement.contains(event.target as Node);
		if (!clickedInside) {
			this.reset();
			this.coordinator.clear(this);
		}
	}

	public reset(): void {
		this.tooltip.hide();
		this.tooltipShownOnMobile.set(false);
	}

	public ngOnDestroy(): void {
		this.coordinator.clear(this);
	}
}
